import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  productsService, 
  ProductListParams, 
  ProductCreateData, 
  ProductUpdateData 
} from '@/services/products.service';
import { toast } from 'sonner';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string') {
    return error.response.data.message;
  }
  return defaultMessage;
}

// Query Keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (params?: ProductListParams) => [...productKeys.lists(), params] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
};

export function useProducts(params?: ProductListParams) {
  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () => productsService.getProducts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => productsService.getProduct(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductCreateData) => productsService.createProduct(data),
    onSuccess: (newProduct) => {
      // Invalidate product lists
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      // Optimistically add to cache
      queryClient.setQueryData(productKeys.detail(newProduct.id), newProduct);
      
      toast.success('Product created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create product'));
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProductUpdateData }) =>
      productsService.updateProduct(id, data),
    onSuccess: (updatedProduct, { id }) => {
      // Update the specific product in cache
      queryClient.setQueryData(productKeys.detail(id), updatedProduct);
      
      // Invalidate product lists to refresh summaries
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      toast.success('Product updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update product'));
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.deleteProduct(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: productKeys.detail(id) });
      
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      toast.success('Product deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete product'));
    },
  });
}

export function useArchiveProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.archiveProduct(id),
    onSuccess: (updatedProduct, id) => {
      // Update the specific product in cache
      queryClient.setQueryData(productKeys.detail(id), updatedProduct);
      
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      toast.success('Product archived successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to archive product'));
    },
  });
}

export function usePublishProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.publishProduct(id),
    onSuccess: (updatedProduct, id) => {
      // Update the specific product in cache
      queryClient.setQueryData(productKeys.detail(id), updatedProduct);
      
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      toast.success('Product published successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to publish product'));
    },
  });
}

// Duplicate Product Mutation
export function useDuplicateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsService.duplicateProduct(id),
    onSuccess: (newProduct) => {
      // Invalidate product lists to show new product
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      
      // Optimistically add to cache
      queryClient.setQueryData(productKeys.detail(newProduct.id), newProduct);
      
      toast.success('Product duplicated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to duplicate product'));
    },
  });
}

// Prefetch Product Hook (for hover/preview)
export function usePrefetchProduct() {
  const queryClient = useQueryClient();

  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: productKeys.detail(id),
      queryFn: () => productsService.getProduct(id),
      staleTime: 5 * 60 * 1000,
    });
  };
}